import { type AttributeLoop, type LoopAttribute, type TagFacet, describeRule, isCategoryTag, matchesAttribute, normalizeTag, tagFacets } from "../../shared/tags";

/**
 * Pure logic behind the Sharing page's filter bar, selection model, and rules: what the worker's sharing
 * overview looks like on the client, how filters narrow it, what a bulk action would change, and how a
 * rule is phrased. The API shapes mirror src/worker/sharing.ts and src/worker/sharingRules.ts; the
 * fields the worker added for scale (source id, kind, tags, per-share rule ids) are optional so the
 * page keeps working against the older overview.
 */

export type SharingShare = { targetOrganizationId: string; ruleId: string | null };

export type SharingLoop = {
  id: string;
  name: string;
  description: string | null;
  cadence: string;
  status: string;
  source: { name: string; provider: string } | null;
  sourceId?: string | null;
  sourceName?: string | null;
  kind?: string | null;
  tags?: string[];
  sharedTo: string[];
  /** One entry per group the loop is shared with; ruleId names the rule that made the grant, null for a hand grant. */
  shares?: SharingShare[];
};

export type SharingGroup = { id: string; name: string; slug: string; role: string };

export type SharingRule = {
  id: string;
  targetOrganizationId: string;
  targetName: string;
  attribute: LoopAttribute;
  value: string;
  sourceName?: string | null;
  description?: string;
  matchCount: number;
  sharedCount: number;
  createdAt: string;
  lastAppliedAt?: string | null;
  createdBy?: { id: string; name: string } | null;
};

export type RuleShape = { attribute: LoopAttribute; value: string; sourceName?: string | null };

export type BulkAction = "share" | "unshare";
export type BulkResult = { shared: number; unshared: number; skipped: Array<{ loopId: string; reason: string }> };

/** The worker's rule suggestions (src/worker/sharingRules.ts suggestRules); every list is optional so the page tolerates an older worker. */
export type RuleSuggestionSource = {
  tags?: TagFacet[];
  sources?: Array<{ id: string; name: string; count?: number; loopCount?: number }>;
  kinds?: Array<{ value: string; count: number }>;
  cadences?: Array<{ value: string; count: number }>;
};

export type SharingFilters = { query: string; tag: string | null; source: string | null; kind: string | null; cadence: string | null };

export const EMPTY_FILTERS: SharingFilters = { query: "", tag: null, source: null, kind: null, cadence: null };

const ATTRIBUTES: LoopAttribute[] = ["tag", "source", "kind", "cadence"];

export function sourceLabel(loop: Pick<SharingLoop, "source" | "sourceName">): string {
  return loop.sourceName ?? loop.source?.name ?? "Manually declared";
}

/** The key a source filter compares: the worker's source id when it reports one, otherwise the source name. */
export function sourceKey(loop: Pick<SharingLoop, "source" | "sourceId" | "sourceName">): string | null {
  return loop.sourceId ?? loop.sourceName ?? loop.source?.name ?? null;
}

export function toAttributeLoop(loop: SharingLoop): AttributeLoop {
  return { tags: loop.tags ?? [], sourceId: sourceKey(loop), kind: loop.kind ?? null, cadence: loop.cadence };
}

function searchText(loop: SharingLoop): string {
  return [loop.name, loop.description ?? "", sourceLabel(loop), loop.kind ?? "", loop.cadence, ...(loop.tags ?? [])].join(" ").toLowerCase();
}

/** Every loop that matches the search and every active attribute filter. */
export function filterSharingLoops(loops: SharingLoop[], filters: SharingFilters): SharingLoop[] {
  const needle = filters.query.trim().toLowerCase();
  const active = activeAttributeFilters(filters);
  return loops.filter((loop) => {
    if (needle && !searchText(loop).includes(needle)) return false;
    const attributes = toAttributeLoop(loop);
    return active.every(({ attribute, value }) => matchesAttribute(attributes, attribute, value));
  });
}

export function activeAttributeFilters(filters: SharingFilters): Array<{ attribute: LoopAttribute; value: string }> {
  return ATTRIBUTES.flatMap((attribute) => {
    const value = filters[attribute];
    return value ? [{ attribute, value }] : [];
  });
}

export type SharingFacets = {
  tags: TagFacet[];
  sources: Array<{ id: string; name: string; count: number }>;
  kinds: Array<{ value: string; count: number }>;
  cadences: Array<{ value: string; count: number }>;
};

function countValues(values: Array<string | null | undefined>): Array<{ value: string; count: number }> {
  const counts = new Map<string, { value: string; count: number }>();
  for (const raw of values) {
    const value = raw?.trim() ?? "";
    if (!value) continue;
    const key = value.toLowerCase();
    const existing = counts.get(key);
    if (existing) existing.count += 1;
    else counts.set(key, { value, count: 1 });
  }
  return [...counts.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

/** Chip counts for the filter bar: category tags (state tags excluded), sources, kinds, and cadences, most common first. */
export function sharingFacets(loops: SharingLoop[]): SharingFacets {
  const sources = new Map<string, { id: string; name: string; count: number }>();
  for (const loop of loops) {
    const id = sourceKey(loop);
    if (!id) continue;
    const existing = sources.get(id);
    if (existing) existing.count += 1;
    else sources.set(id, { id, name: sourceLabel(loop), count: 1 });
  }
  return {
    tags: tagFacets(loops.map((loop) => ({ tags: loop.tags ?? [] })), 12),
    sources: [...sources.values()].sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
    kinds: countValues(loops.map((loop) => loop.kind)),
    cadences: countValues(loops.map((loop) => loop.cadence)),
  };
}

/** "3 of 12 loops" while a filter narrows the view, otherwise just the count. */
export function shownLine(shown: number, total: number): string {
  const noun = total === 1 ? "loop" : "loops";
  return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`;
}

export function countLabel(count: number, verb: string, noun = "loop"): string {
  return `${verb} ${count} ${count === 1 ? noun : `${noun}s`}`;
}

export function toggleId(selected: ReadonlySet<string>, id: string): Set<string> {
  const next = new Set(selected);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/** The selection restricted to the rows still on screen, so a filter change never acts on hidden loops. */
export function selectionWithin(selected: ReadonlySet<string>, visibleIds: Iterable<string>): Set<string> {
  const next = new Set<string>();
  for (const id of visibleIds) if (selected.has(id)) next.add(id);
  return next;
}

/** What a bulk action would change for the chosen group: loops not yet shared with it, and loops currently shared with it. */
export function bulkPlan(loops: SharingLoop[], selected: ReadonlySet<string>, groupId: string): { share: string[]; unshare: string[] } {
  const share: string[] = [];
  const unshare: string[] = [];
  for (const loop of loops) {
    if (!selected.has(loop.id)) continue;
    (loop.sharedTo.includes(groupId) ? unshare : share).push(loop.id);
  }
  return { share, unshare };
}

/** The rule that made a loop's share with a group, or null for a hand grant (and for the older overview without shares). */
export function shareRuleId(loop: SharingLoop, groupId: string): string | null {
  return loop.shares?.find((share) => share.targetOrganizationId === groupId)?.ruleId ?? null;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/** "Every loop tagged growth is shared with Acme Product". */
export function ruleSentence(rule: RuleShape, targetName: string): string {
  return `${capitalize(describeRule(rule.attribute, rule.value, rule.sourceName))} is shared with ${targetName}`;
}

/** Hover title for the "rule" chip on a share cell. */
export function ruleTitle(rule: RuleShape): string {
  return `Shared by rule: ${describeRule(rule.attribute, rule.value, rule.sourceName)}`;
}

/** After a bulk share: the rule to offer when exactly one attribute filter shaped the selection. */
export function ruleOffer(filters: SharingFilters): { attribute: LoopAttribute; value: string } | null {
  const active = activeAttributeFilters(filters);
  return active.length === 1 ? active[0] : null;
}

/** How many loops a rule would match right now. */
export function matchCount(loops: SharingLoop[], attribute: LoopAttribute, value: string): number {
  if (!value.trim()) return 0;
  return loops.filter((loop) => matchesAttribute(toAttributeLoop(loop), attribute, value)).length;
}

export type RuleSuggestion = { value: string; label: string; count: number };
export type RuleSuggestions = Record<LoopAttribute, RuleSuggestion[]>;

/**
 * Values to offer in the New rule form. Each list comes from the worker's suggestions when it reports
 * them, otherwise from the catalog on the page. Source rules match on the worker's source id, so they
 * are only suggested once ids are reported.
 */
export function ruleSuggestions(loops: SharingLoop[], server?: RuleSuggestionSource | null): RuleSuggestions {
  const facets = sharingFacets(loops);
  const valued = (entries: Array<{ value: string; count: number }> | undefined) => entries?.map((entry) => ({ value: entry.value, label: entry.value, count: entry.count }));
  const serverTags = server?.tags?.map((facet) => ({ value: facet.tag, label: facet.tag, count: facet.count }));
  const serverSources = server?.sources?.map((source) => ({ value: source.id, label: source.name, count: source.count ?? source.loopCount ?? 0 }));
  const serverKinds = valued(server?.kinds);
  const serverCadences = valued(server?.cadences);
  const localSources = loops.some((loop) => loop.sourceId) ? facets.sources.map((source) => ({ value: source.id, label: source.name, count: source.count })) : [];
  return {
    tag: serverTags?.length ? serverTags : facets.tags.map((facet) => ({ value: facet.tag, label: facet.tag, count: facet.count })),
    source: serverSources?.length ? serverSources : localSources,
    kind: serverKinds?.length ? serverKinds : valued(facets.kinds) ?? [],
    cadence: serverCadences?.length ? serverCadences : valued(facets.cadences) ?? [],
  };
}

/**
 * The rule behind each share the viewer made, keyed "loopId:groupId", from their personal overview and
 * rules. A group's loop list does not say which rule shared a loop in, so this is how the viewer's own
 * rule-made shares get their label there; shares other members made stay unlabelled until the worker
 * reports the rule on the loop.
 */
export function ownRuleShares(loops: SharingLoop[], rules: SharingRule[]): Map<string, RuleShape> {
  const byId = new Map(rules.map((rule) => [rule.id, rule]));
  const shares = new Map<string, RuleShape>();
  for (const loop of loops) {
    for (const share of loop.shares ?? []) {
      const rule = share.ruleId ? byId.get(share.ruleId) : null;
      if (rule) shares.set(`${loop.id}:${share.targetOrganizationId}`, { attribute: rule.attribute, value: rule.value, sourceName: rule.sourceName ?? null });
    }
  }
  return shares;
}

/** The rule a group sees on a shared loop: what the worker reported on the loop, else the viewer's own rule for that share, else none. */
export function ruleForSharedLoop(loop: { id: string; visibility: { rule?: RuleShape | null } }, groupId: string, own: ReadonlyMap<string, RuleShape>): RuleShape | null {
  return loop.visibility.rule ?? own.get(`${loop.id}:${groupId}`) ?? null;
}

/** One line after a bulk action: what changed, and how many loops the worker skipped. */
export function bulkOutcomeLine(result: BulkResult, action: BulkAction, groupName: string): string {
  const skipped = result.skipped.length ? ` · ${result.skipped.length} skipped` : "";
  if (action === "share" && result.shared > 0) return `${countLabel(result.shared, "Shared")} with ${groupName}${skipped}.`;
  if (action === "unshare" && result.unshared > 0) return `${countLabel(result.unshared, "Unshared")} from ${groupName}${skipped}.`;
  return `Nothing changed${skipped}.`;
}

/** The normalized tag a facet chip carries, so a click filters exactly what tagFacets counted. */
export function facetTag(tag: string): string {
  return normalizeTag(tag);
}

/** The category tags a card or row shows: up to `limit` distinct ones, then how many more there are. State tags never appear. */
export function visibleTags(tags: string[] | null | undefined, limit = 3): { shown: string[]; more: number } {
  const seen = new Set<string>();
  const category: string[] = [];
  for (const raw of tags ?? []) {
    const tag = normalizeTag(raw);
    if (!isCategoryTag(tag) || seen.has(tag)) continue;
    seen.add(tag);
    category.push(tag);
  }
  return { shown: category.slice(0, limit), more: Math.max(0, category.length - limit) };
}

/** The origin line a group sees on a shared loop: who shared it, and the rule when one made the grant. */
export function sharedByLine(origin: { ownerName?: string | null; originName: string; rule?: RuleShape | null }): string {
  const lead = origin.ownerName ? `Shared by ${origin.ownerName}` : `Shared from ${origin.originName}`;
  return origin.rule ? `${lead} · by rule: ${describeRule(origin.rule.attribute, origin.rule.value, origin.rule.sourceName)}` : lead;
}
